import type { Vector } from "@common/utils/vector";
import { shoreOffset, shorelineRuns, type ShorePoint } from "./shorelineGeometry";

export interface PaintedShoreSource { points: readonly Vector[]; river?: boolean }
export interface PaintedShoreGeometry { positions: Float32Array; uvs: Float32Array; indices: Uint32Array; columns: number }
export const SHORE_COLUMN_LIMIT = 6000;
const TURF_WIDTH = 22, SURF_WIDTH = 25;
const TURF_BOUNDARY = 0.48, SURF_BOUNDARY = 0.36;

/** Fixed texture coordinates follow true bank edges; no lighting, terrain queries or buffer edits per frame. */
export function paintedShoreGeometry(sources: readonly PaintedShoreSource[], scale: number, water: boolean,
    waterAt?: (point: Vector) => boolean, sandAt?: (point: Vector) => boolean): PaintedShoreGeometry {
    const paths = sources.flatMap(source => {
        const runs = shorelineRuns(source.points, !!source.river, water ? waterAt : sandAt, scale === 1 ? 7 : 4);
        // Join the two ends of a clipped closed coast, never across an actual mouth.
        if (!source.river && runs.length > 1) {
            const first = runs[0], last = runs[runs.length - 1];
            if (Math.hypot(first[0].x - last[last.length - 1].x, first[0].y - last[last.length - 1].y) < 8) {
                runs[0] = [...last, ...first]; runs.pop();
            }
        }
        return runs.map(run => ({ run, canClose: !source.river }));
    });
    const total = paths.reduce((sum, path) => sum + path.run.length, 0);
    const step = Math.max(1, Math.ceil(total / (SHORE_COLUMN_LIMIT - paths.length * 2)));
    const positions: number[] = [], uvs: number[] = [], indices: number[] = [];
    const rows = water ? [0, 0.2, SURF_BOUNDARY, 0.65, 1] : [0, 1];
    const boundary = water ? SURF_BOUNDARY : TURF_BOUNDARY;
    const width = water ? SURF_WIDTH : TURF_WIDTH;
    const landAt = waterAt ? (p: Vector): boolean => !waterAt(p) : undefined;
    let columns = 0;
    for (const path of paths) {
        if (columns + 2 >= SHORE_COLUMN_LIMIT) break;
        const sampled = path.run.filter((_, i) => i % step === 0 || i === path.run.length - 1);
        const loop = path.canClose && Math.hypot(sampled[0].x - sampled[sampled.length - 1].x,
            sampled[0].y - sampled[sampled.length - 1].y) < 8;
        const corners = sampled.filter((p, i) => {
            if (!loop && (i === 0 || i === sampled.length - 1)) return false;
            const a = sampled[(i + sampled.length - 1) % sampled.length], b = sampled[(i + 1) % sampled.length];
            const ax = p.x - a.x, ay = p.y - a.y, bx = b.x - p.x, by = b.y - p.y;
            return (ax * bx + ay * by) / (Math.hypot(ax, ay) * Math.hypot(bx, by)) < 0.5;
        });
        // Dense straight-edge samples inside a miter would double back past its
        // inner corner. Reserve enough room for the whole ribbon to make the turn.
        const run = sampled.filter(p => corners.includes(p) || !corners.some(c => Math.hypot(p.x - c.x, p.y - c.y) < width));
        if (run.length < 2) continue;
        const closed = loop;
        if (closed) run.push(run[0]);
        let length = 0;
        for (let i = 1; i < run.length; i++) length += Math.hypot(run[i].x - run[i - 1].x, run[i].y - run[i - 1].y);
        // An even number of mirrored repeats joins the first/last UV on a closed coast.
        const repeats = closed ? Math.max(2, 2 * Math.round(length / 144)) : length / 72;
        let distance = 0, previousColumn: number | undefined;
        for (let i = 0; i < run.length && columns < SHORE_COLUMN_LIMIT; i++) {
            const point = run[i];
            if (i) distance += Math.hypot(point.x - run[i - 1].x, point.y - run[i - 1].y);
            const before = run[i ? i - 1 : closed ? run.length - 2 : 0];
            const after = run[i < run.length - 1 ? i + 1 : closed ? 1 : i];
            // Shared miters avoid folded inner triangles where horizontal and vertical banks meet.
            const incoming = Math.hypot(point.x - before.x, point.y - before.y);
            const outgoing = Math.hypot(after.x - point.x, after.y - point.y);
            let nx = point.nx, ny = point.ny, miter = 1;
            if (incoming > 0.001 && outgoing > 0.001) {
                const ax = -(point.y - before.y) / incoming, ay = (point.x - before.x) / incoming;
                const bx = -(after.y - point.y) / outgoing, by = (after.x - point.x) / outgoing;
                const size = Math.hypot(ax + bx, ay + by);
                if (size > 0.1) {
                    nx = (ax + bx) / size; ny = (ay + by) / size;
                    miter = Math.min(1.6, 1 / Math.max(0.1, Math.abs(nx * bx + ny * by)));
                    if (nx * point.nx + ny * point.ny < 0) { nx = -nx; ny = -ny; }
                }
            }
            const p: ShorePoint = { ...point, nx, ny };
            // Collapse open ends so the strip cannot project a square cap into open sea.
            const taper = closed ? 1 : Math.min(1, i / 2, (run.length - 1 - i) / 2);
            const column = rows.map(v => {
                const offset = (v - boundary) * width * taper * miter;
                if (Math.abs(offset) < 0.01) return p;
                return shoreOffset(p, offset, water ? offset > 0 ? waterAt : landAt : undefined);
            });
            if (column.some(q => q === undefined)) { previousColumn = undefined; continue; }
            const base = positions.length / 2;
            for (let j = 0; j < column.length; j++) {
                const q = column[j];
                if (!q) continue;
                positions.push(q.x * scale, q.y * scale);
                uvs.push(length ? distance / length * repeats : 0, rows[j]);
            }
            if (previousColumn !== undefined) for (let row = 0; row < rows.length - 1; row++) {
                indices.push(previousColumn + row, base + row, base + row + 1,
                    previousColumn + row, base + row + 1, previousColumn + row + 1);
            }
            previousColumn = base;
            columns++;
        }
    }
    return { positions: new Float32Array(positions), uvs: new Float32Array(uvs), indices: new Uint32Array(indices), columns };
}
